import FJSprite from './FJSprite';
import FJSubtitleText from './FJSubtitleText';
import FJUtil from '../util/FJUtil';
import { debounce } from 'lodash-es';
import FJMathUtil from '../util/FJMathUtil.js';
import FJArchiveUtil from '../util/FJArchiveUtil';
import FJArrayUtil from '../util/FJArrayUtil';
import FJViewConstant from '../constants/FJViewConstant.js';
import FJCoreConfig from './FJCoreConfig.js';

// 字幕对象
class FJSubtitle extends FJSprite {
    constructor(props) {
        super(props);
        this._type = 'subtitle';
        this._elements = [];
        this.adaptiveRelativeRectDebounce = debounce(this.adaptiveRelativeRect.bind(this), 100, {
            trailing: true,
        });
        this.adaptiveAttrDebounce = debounce(this.adaptiveAttr.bind(this), 100, {
            trailing: true,
        });
        this.adaptiveAfterResizeDebounce = debounce(this.adaptiveAfterResize.bind(this), 100, {
            trailing: true,
        });
        this._isPlaying = false;
        this._stylePresetId = 'None';
    }

    set stylePresetId(stylePresetId) {
        this._stylePresetId = stylePresetId;
    }

    get stylePresetId() {
        return this._stylePresetId;
    }

    encode() {
        return this.toJSON();
    }

    async decode(json) {
        const texts = json.elements;
        for (let i = 0; i < texts.length; i++) {
            let data = texts[i];
            let element = new FJSubtitleText();
            await element.decode(data);
            this.addElement(element);
        }
        this.stylePresetId = json.elements && json.elements.length > 0 ? json.stylePresetId : 'None';
    }

    async decodeRely(json) {
        await Promise.all(
            json.elements.map(data => {
                const element = new FJSubtitleText();
                const index = this.getAllElements().indexOf(
                    this.getAllElements().find(i => i.getSubtitleTextDelay() > data.subtitleTextDelay),
                );
                if (index >= 0) {
                    this.addElement(element, index);
                } else {
                    this.addElement(element);
                }
                return element.decodeRely(data);
            }),
        );
        this.stylePresetId = json.elements && json.elements.length > 0 ? json.stylePresetId : 'None';
    }

    toJSON() {
        return {
            id: this.getId(),
            type: this.getType(),
            elements: this.getAllElements().map(element => element.toJSON()),
            stylePresetId: this.stylePresetId,
        };
    }

    fromJSON(data, graphCache) {
        // 如果没有数据，清空所有元素
        if (!data) {
            this.clear();
            return;
        }

        const removeElements = [];
        const restElements = [];

        const allElements = this.getAllElements();
        allElements.forEach(element => {
            if (!data.elements.find(data => data.id === element.getId())) {
                removeElements.push(element);
            } else {
                restElements.push(element);
            }
        });

        removeElements.forEach(element => element.removeFromView());

        this._type = data.type;
        this._id = data.id;
        this._elements.length = 0;
        data.elements.forEach(elementData => {
            const element = graphCache[elementData.id] || restElements.find(ele => ele.getId() === elementData.id);
            element.fromJSON(elementData);
            this._elements.push(element);
        });
        this.stylePresetId = data.stylePresetId;
    }

    clear() {
        this.removeAllFromView();
        this._elements.length = 0;
    }

    isEmpty() {
        return this._elements.length === 0;
    }

    getAllElements() {
        return this._elements;
    }

    /**
     * @desc 字幕是否为空
     * @returns
     */
    hasEmpty() {
        return this._elements.length === 0;
    }

    addElement(element, insertIndex) {
        FJArrayUtil.addToArray(this.getAllElements(), element, insertIndex);
    }
    addElements(elements) {
        this.getAllElements().push(...elements);
    }

    coverAddElements(json) {
        let elements = json.elements;
        let allElements = this.getAllElements();
        elements.forEach(subtitle => {
            const subtitleDelay = subtitle.subtitleTextDelay;
            const subtitleDuration = subtitle.subtitleTextDuration;
            const subtitleEndDelay = subtitleDelay + subtitleDuration;

            for (let index = 0; index < allElements.length; index++) {
                const item = allElements[index];
                let delay = item.getSubtitleTextDelay();
                const duration = item.getSubtitleTextDuration();
                const endDelay = delay + duration;
                if (delay <= subtitleDelay && endDelay > subtitleDelay && endDelay <= subtitleEndDelay) {
                    item.setSubtitleTextDuration(subtitleDelay - delay);
                    if (subtitleDelay - delay < FJCoreConfig.minMediaDuration) {
                        this.deleteElement(item.getId());
                        index--;
                    }
                } else if (delay <= subtitleDelay && endDelay > subtitleEndDelay) {
                    item.setSubtitleTextDuration(subtitleDelay - delay);
                    if (subtitleDelay - delay < FJCoreConfig.minMediaDuration) {
                        this.deleteElement(item.getId());
                        index--;
                    }

                    const newElement = item.clone();
                    newElement.setSubtitleTextDelay(subtitleEndDelay);
                    newElement.setSubtitleTextDuration(endDelay - subtitleEndDelay);
                    if (endDelay - subtitleDelay >= FJCoreConfig.minMediaDuration) {
                        const idx = this.getAllElements().indexOf(
                            this.getAllElements().find(i => i.getSubtitleTextDelay() > subtitleEndDelay),
                        );
                        if (idx > -1) {
                            this.addElement(newElement, idx);
                        } else {
                            this.addElement(newElement);
                        }
                    }
                    //插入
                } else if (delay >= subtitleDelay && endDelay <= subtitleEndDelay) {
                    //删除
                    this.deleteElement(item.getId());
                } else if (delay >= subtitleDelay && delay < subtitleEndDelay && endDelay > subtitleEndDelay) {
                    item.setSubtitleTextDelay(subtitleEndDelay);
                    item.setSubtitleTextDuration(endDelay - subtitleEndDelay);
                    if (endDelay - subtitleDelay < FJCoreConfig.minMediaDuration) {
                        this.deleteElement(item.getId());
                        index--;
                    }
                }
            }
        });
        return this.decodeRely(json);
    }
    getElement(id) {
        return this.getAllElements().find(element => element.getId() === id);
    }

    getElementByIndex(index) {
        return this.getAllElements()[index];
    }

    getElementIndexById(id) {
        let index = -1;
        for (let i = 0; i < this.getAllElements().length; i++) {
            let e = this.getAllElements()[i];
            if (e.getId() === id) {
                index = i;
                break;
            }
        }
        return index;
    }

    /**
     * @description 返回当前时间应该渲染的元素
     * @param {number} time
     */
    getElementToRender(time) {
        let renderText = null;
        const textListToRender = this.getElementsByTime(time);

        // 只渲染一个元素
        // 优先考虑选中状态，其次考虑右侧元素
        if (textListToRender.length !== 0) {
            const seletedList = textListToRender.filter(text => text.isSelected());
            if (seletedList.length === 0) {
                renderText = FJArrayUtil.tailOfArrayLike(textListToRender);
            } else {
                renderText = FJArrayUtil.tailOfArrayLike(seletedList);
            }
        }

        return renderText;
    }

    /**
     * @description 返回符合时间线的元素数组
     * @param {*} time
     * @returns
     */
    getElementsByTime(time) {
        return this.getAllElements().filter(text => this.useTimePatchElement(text, time));
    }

    deleteElement(id) {
        const element = this.getElement(id);
        if (element) {
            element.removeFromView();
            FJArrayUtil.removeFromArray(this.getAllElements(), element);
        }
    }
    deleteAllElement() {
        while (this.getAllElements().length) {
            const element = this.getAllElements()[0];
            element.removeFromView();
            FJArrayUtil.removeFromArray(this.getAllElements(), element);
        }
    }
    removeAllFromView() {
        this.getAllElements().forEach(element => element.removeFromView());
    }

    /**
     * @description 判断元素是否在指定时间出现
     * @param element
     * @param {number | undefined} time
     * @return {boolean|boolean}
     */
    useTimePatchElement(element, time) {
        const curTime = time;
        const delay = element.getSubtitleTextDelay();
        const duration = element.getSubtitleTextDuration();
        return duration > 0 && curTime >= delay && curTime <= delay + duration;
    }

    /**
     * @description 当前选中的元素
     * @returns
     */
    selectedElement() {
        return this.getAllElements().find(element => element.isSelected());
    }

    /**
     * @description 当前选中的元素数组
     * @returns
     */
    selectedElements() {
        return this.getAllElements().filter(element => element.isSelected());
    }

    /**'
     * @description 选择元素
     * @param {string} id 元素 id
     * @param {boolean} isMulti 是否是多选（值为 true 的则不取消当前选中的元素）
     */
    selectElement(id, isMulti) {
        this.getAllElements().forEach(element => {
            if (element.getId() === id) {
                element.select();
            } else if (element.isSelected() && !isMulti) {
                element.deselect();
            }
        });
    }

    drawTrack(curTime) {
        const elementToRender = this.getElementToRender(curTime);
        this.getAllElements().forEach(element => {
            if (elementToRender === element && element.isSelected()) {
                element.updateTrack(FJArchiveUtil.getAuxiliary());
            } else {
                element.clearTrack();
            }
        });
    }

    clearTrack() {
        this.getAllElements().forEach(ele => {
            ele.clearTrack();
        });
    }

    deselectAll() {
        this.getAllElements().forEach(element => {
            element.deselect();
        });
    }

    hoverElement(id) {
        this.getAllElements().forEach(element => {
            if (element.getId() === id) {
                if (!element.isSelected()) {
                    element.drawTrackWithMouseOver(FJArchiveUtil.getAuxiliary());
                }
            } else if (!element.isSelected()) {
                element.clearTrack();
            }
        });
    }

    _getElementWithPoint(point, time, includeRotate) {
        return this.getAllElements()
            .filter(element => this.useTimePatchElement(element, time))
            .find(element => {
                if (includeRotate) {
                    return element.isPointInElement(point) || element.isPointInElementSpecialNode(point);
                } else {
                    return element.isPointInElement(point);
                }
            });
    }

    /**
     * @desc 鼠标点击事件
     * @param point - 鼠标的位置
     * @param viewSize - 画布的大小
     * @param {object} options
     * @param {number} options.time
     */
    hitTest(point, viewSize, options = {}) {
        const { time } = options;
        let trackNode = -1;
        let selectedElement = null;
        let elementRect = null;
        const element = this._getElementWithPoint(point, time);

        if (element) {
            selectedElement = element;
            elementRect = FJMathUtil.relativeRectToRect(element.getRelativeRect(), viewSize);
            trackNode = element.getTrackNodeByPoint(point);
        }

        this.selectElement(element?.getId());

        return {
            selectedElement,
            trackNode,
            elementRect,
        };
    }

    /**
     * @description 移动元素
     * @param offset 移动的偏移量
     * @param originalRect
     * @param viewSize
     * @param options
     * @param options.shouldToCover
     * @return {*}
     */
    move(offset, originalRect, viewSize, options) {
        let element = this.selectedElement();
        if (element) {
            const result = element.move(offset, originalRect, viewSize, [], true, options);
            this.adaptiveRelativeRectDebounce(element.getId());
            return result;
        }
    }

    /**
     * @description 旋转元素
     * @param currentPoint
     * @param oldPoint
     * @param viewSize
     * @param needAdsorbVal
     * @param options
     * @param options.shouldToCover
     * @return {*}
     */
    rotate(currentPoint, oldPoint, viewSize, needAdsorbVal = true, options) {
        let element = this.selectedElement();
        if (element) {
            const result = element.rotate(currentPoint, oldPoint, viewSize, needAdsorbVal, options);
            this.adaptiveRelativeRectDebounce(element.getId());
            return result;
        }
    }

    /**
     * @description 缩放元素
     * @param offset
     * @param node
     * @param viewSize
     * @param options
     * @param options.shouldToCover
     * @return {*}
     */
    resize(offset, node, viewSize, options) {
        let element = this.selectedElement();
        if (element) {
            const result = element.resize(offset, node, viewSize, options);
            this.adaptiveAfterResizeDebounce(element.getId(), viewSize);
            return result;
        }
    }

    /**
     * @desc 鼠标 hover
     * @param point - 鼠标的位置
     * @param viewSize - 画布的大小
     * @param {object} options
     * @param {number} options.time
     */
    mouseOver(point, options) {
        const { time, realTimeInputElementId } = options;
        const element = this._getElementWithPoint(point, time, true);
        this.hoverElement(element?.getId());

        if (realTimeInputElementId && realTimeInputElementId === element?.getId()) {
            if (element.isPointInElementSpecialNode(point)) {
                element && element.changeCursorByPoint(point);
            }
            return !!element;
        } else {
            element && element.changeCursorByPoint(point);
        }
        return !!element;
    }

    play(view, time) {
        if (this._isPlaying) {
            return;
        }
        this._isPlaying = true;
        const renderText = this.getElementToRender(time);
        const otherTexts = this.getAllElements().filter(text => text !== renderText);
        otherTexts.forEach(text => {
            text.reset();
            text.removeFromView(true);
            text.clearTrack();
        });

        let currentRenderId = null;
        if (renderText) {
            renderText.addToView(view);
            const delay = renderText.getSubtitleTextDelay();
            renderText.play(time - delay);
            currentRenderId = renderText.getId();
        }

        let startTime = null;
        const __play = timestamp => {
            try {
                startTime = startTime || timestamp;
                const currentTime = time + (timestamp - startTime) / 1000;
                const renderText = this.getElementToRender(currentTime);
                const otherTexts = this.getAllElements().filter(text => text !== renderText);
                otherTexts.forEach(text => {
                    text.reset(true);
                    text.removeFromView(true);
                    text.clearTrack();
                });
                if (renderText && currentRenderId !== renderText.getId()) {
                    renderText.addToView(view);
                    renderText.play(0);
                    currentRenderId = renderText.getId();
                }

                if (this._isPlaying) this.requestTimer = window.requestAnimationFrame(__play);
            } catch (e) {
                console.log(e);
            }
        };
        this.requestTimer = window.requestAnimationFrame(__play);
    }

    pause() {
        this._isPlaying = false;
        this.getAllElements().forEach(text => text.pause());
        if (this.requestTimer) {
            window.cancelAnimationFrame(this.requestTimer);
        }
    }

    seek(time) {
        if (this._isPlaying) {
            return;
        }
        const renderText = this.getElementToRender(time);
        if (this.currentRenderId !== renderText?.getId()) {
            renderText && renderText.initAnimation();
            this.currentRenderId = renderText?.getId();
        }
        renderText && renderText.seek(time - renderText.getSubtitleTextDelay());
    }

    /**
     * @description 渲染
     * @param {HTMLElement} view 视图
     * @param {number} time 时间
     * @param {{}} options
     * @param {boolean} options.flush 是否强制绘制，如果是的话会先将所有元素移出视图
     * @param {boolean} options.shouldBlockSelected 是否强制绘制选中的元素
     * @param {boolean} options.subToCover 是否需要覆盖
     */
    render(view, time, options = {}) {
        const { flush, shouldBlockSelected, subToCover } = options;
        const renderText = (shouldBlockSelected && this.selectedElement()) || this.getElementToRender(time);
        const otherTexts = this.getAllElements().filter(text => flush || text !== renderText);
        otherTexts.forEach(text => {
            text.removeFromView(true);
            text.clearTrack();
        });
        renderText && renderText.addToView(view);

        if (subToCover) {
            renderText?.goToCoverFrame();
        } else {
            renderText && renderText.seek(time - renderText.getSubtitleTextDelay());
        }
    }

    /**
     * @description 获取属性
     * @returns {{scale: (*|number), isBold: (*|boolean), isItalic: (*|boolean), duration: (*|number), textData: *, fontFamily: (*|string), delay: (*|number), mainColor: *, relativeRect: *, angle: (number|*), subColor: *, alignment: (*|string), endDelay: (*|number)}|{scale: number, isBold: boolean, isItalic: boolean, duration: number, textData: any, fontFamily: string, delay: number, mainColor: string, relativeRect: any, angle: number, fontSize: *, subColor: string, alignment: string, endDelay: number}|*}
     */
    getAttr() {
        return this.getAllElements()[0].getAllAttr();
    }

    /**
     * @description 设置属性
     * @param type
     * @param data
     */
    setAttr(type, data, options = {}) {
        const elementList = this.getAllElements();
        const { isNeedUpdate } = options;
        elementList.forEach(element => {
            switch (type) {
                case 'fontFamily':
                case 'fontSize':
                case 'fontColor':
                case 'padding':
                case 'background':
                case 'fontBold':
                case 'fontItalic':
                case 'alignment':
                case 'lineHeight':
                case 'spacing': {
                    element.updateWithAttribute(data);
                    break;
                }
                case 'opacity': {
                    element.opacity(data.opacity);
                    break;
                }
                case 'style': {
                    element.setStyleName(data.type, data.info);
                    break;
                }
                case 'subtitleTextStyledAdjust': {
                    element.setTextStyleAdjustInfo(data.info, isNeedUpdate);
                    break;
                }
                case FJViewConstant.SHAPE_COLOR:
                    element.animationBackgroundColor = data.color;
                    element.updateSubtitleAnimation();
                    break;
                case FJViewConstant.HIGHLIGHT_COLOR:
                    element.animationTextColor = data.color;
                    element.updateSubtitleAnimation();
                    break;
                case 'anchor':
                    element.anchorTextDirection = data.anchorTextDirection;
                    break;
                case 'textCombinationType':
                    element.textCombinationType = data.textCombinationType;
                    break;
                default:
                    element.updateWithAttribute(data);
            }
        });

        this.adaptiveRelativeRectDebounce();
    }

    /**
     * @description 返回指定元素的可活动返回
     * @param {string} id
     * @param {number} projectDuration
     * @returns
     */
    getLimitInfo(id, projectDuration) {
        const info = [0, projectDuration];
        const allElements = this.getAllElements();
        const element = this.getElement(id);
        const index = allElements.indexOf(element);
        const preElement = allElements[index - 1];
        const nextElement = allElements[index + 1];
        if (preElement) {
            info[0] = preElement.getSubtitleTextDelay() + preElement.getSubtitleTextDuration();
        }
        if (nextElement) {
            info[1] = nextElement.getSubtitleTextDelay();
        }
        return info;
    }

    /**
     * @description 合并指定 elementA、elementB
     * @param {*} elementA
     * @param {*} elementB
     * @returns {string} id
     */
    mergeElements(elementA, elementB) {
        const mergedDuration =
            elementB.getSubtitleTextDelay() + elementB.getSubtitleTextDuration() - elementA.getSubtitleTextDelay();
        elementA.setSubtitleTextDuration(mergedDuration);

        const newTextData = [];
        let mergedText = '';
        const textDataA = elementA.textData;
        const textDataB = elementB.textData;

        const tailA = FJArrayUtil.tailOfArrayLike(textDataA);
        const headB = FJArrayUtil.headOfArrayLike(textDataB);
        const tailChar = FJArrayUtil.tailOfArrayLike(tailA);
        const headChar = FJArrayUtil.headOfArrayLike(headB);

        let cursorPosition = textDataA.join('\n').length;

        // 只有当前需要合并的两个 string 之间没有空格时才需要补一个空格
        if (tailChar !== ' ' && headChar !== ' ') {
            mergedText = `${tailA} ${headB}`;
            cursorPosition++;
        } else {
            mergedText = `${tailA}${headB}`;
        }
        textDataA.forEach((text, index) => {
            if (index !== textDataA.length - 1) {
                newTextData.push(text);
            }
        });
        newTextData.push(mergedText);
        textDataB.forEach((text, index) => {
            if (index !== 0) {
                newTextData.push(text);
            }
        });
        if (elementB.getBind() || elementA.getBind()) {
            elementA.setBind(true);
        }
        elementA.wordTimestampList = [];
        elementA.updateWithAttribute({ textData: [...newTextData] });
        this.deleteElement(elementB.getId());
        this.selectElement(elementA.getId());

        const id = elementA.getId();
        this.adaptiveRelativeRectDebounce(id);
        return {
            id,
            cursorPosition,
        };
    }

    adaptiveAfterResize(id, viewSize) {
        this.adaptiveAttr(id);
        this.adaptiveRelativeRect(id, viewSize);
    }

    /**
     * @description 适配字幕属性
     * @param id
     */
    adaptiveAttr(id) {
        const allElements = this.getAllElements();
        const element = FJUtil.isUndefined(id) ? this.selectedElement() || allElements[0] : this.getElement(id);
        if (!element) {
            return;
        }
        const adaptiveInfo = element.getAllAttr();
        allElements.forEach(ele => {
            if (ele && ele !== element) {
                ele.setAllAttr({ ...adaptiveInfo });
            }
        });
    }

    updateRelativeRect(viewSize, hasUpdateNode = false) {
        const allElements = this.getAllElements();
        allElements.forEach(element => {
            element.updateRelativeRect(viewSize, hasUpdateNode);
            // 与 addToView 一致：仅 updateRelativeRect 不会刷新 layer 的 left/top；全屏 resize 时必须 updateView
            if (viewSize && viewSize.width > 0 && viewSize.height > 0) {
                element.updateView(viewSize, true);
            }
        });
    }

    /**
     * @description 其他字幕元素自适应对齐指定 id 的字幕对象
     * @param {string | undefined} id - 没有传递 id 的话会自动获取当前选中的元素的 id，如果没有选中的话会获取首个元素的 id
     * @param viewSize
     */
    adaptiveRelativeRect(id, viewSize) {
        const allElements = this.getAllElements();
        const element = FJUtil.isUndefined(id) ? this.selectedElement() || allElements[0] : this.getElement(id);
        if (!element) {
            return;
        }
        const { x, y, width, height } = element.getRelativeRect();
        const angle = element.angle();
        allElements.forEach(ele => {
            if (ele && ele !== element) {
                const { x: cX, y: cY, width: cWidth, height: cHeight } = ele.getRelativeRect();
                const cAngle = ele.angle();
                if (cX !== x || cY !== y || cWidth !== width || cHeight !== height || cAngle !== angle) {
                    const verticalMiddleY = y + height / 2;
                    const newX = x;
                    const newWidth = width;
                    const newHeight = cHeight;
                    const newY = verticalMiddleY - newHeight / 2;
                    ele.setRelativeRect({
                        x: newX,
                        y: newY,
                        width: newWidth,
                        height: newHeight,
                    });
                    ele.angle(angle);
                    ele.updateRelativeRectWithElementSize();
                    ele.updateSubtitleAnimation(viewSize);
                }
            }
        });
    }

    splitElementById(id, cursorPosition) {
        const element = this.getElement(id);
        const elementText = element.textData.join('\n');
        const subtitleTextDelay = element.getSubtitleTextDelay();
        const subtitleTextDuration = element.getSubtitleTextDuration();
        const textA = elementText.slice(0, cursorPosition);
        const textB = elementText.slice(cursorPosition);
        const textADelay = subtitleTextDelay;
        const textADuration = FJMathUtil.toFixed(subtitleTextDuration * (textA.length / elementText.length));
        const textBDelay = FJMathUtil.toFixed(subtitleTextDelay + textADuration);
        const textBDuration = FJMathUtil.toFixed(subtitleTextDuration - textADuration);
        const newElement = element.clone();
        element.setSubtitleTextDuration(textADuration);
        element.setSubtitleTextDelay(textADelay);
        element.wordTimestampList = [];
        element.updateWithAttribute({ textData: textA.split('\n') });
        newElement.setSubtitleTextDuration(textBDuration);
        newElement.setSubtitleTextDelay(textBDelay);
        newElement.wordTimestampList = [];
        newElement.updateWithAttribute({ textData: textB.split('\n') });
        const insertIndex = this.getElementIndexById(id) + 1;
        this.addElement(newElement, insertIndex);
        this.selectElement(newElement.getId());
        return newElement.getId();
    }

    splitElementByCurrentTime(id, currentTime) {
        const element = this.getElement(id);
        const elementText = element.textData.join('\n');
        const subtitleTextDelay = element.getSubtitleTextDelay();
        const subtitleTextDuration = element.getSubtitleTextDuration();
        const textADelay = subtitleTextDelay;
        const textADuration = FJMathUtil.toFixed(currentTime - subtitleTextDelay);
        const textBDelay = FJMathUtil.toFixed(currentTime);
        const textBDuration = FJMathUtil.toFixed(subtitleTextDuration + subtitleTextDelay - currentTime);
        let curPosition = Math.floor((elementText.length * (currentTime - subtitleTextDelay)) / subtitleTextDuration);
        curPosition = FJMathUtil.getValueInRange(curPosition, 0, elementText.length);
        const textA = elementText.slice(0, curPosition);
        const textB = elementText.slice(curPosition);
        const newElement = element.clone();
        element.updateWithAttribute({ textData: textA.split('\n') });
        element.setSubtitleTextDuration(textADuration);
        element.setSubtitleTextDelay(textADelay);
        newElement.updateWithAttribute({ textData: textB.split('\n') });
        newElement.setSubtitleTextDuration(textBDuration);
        newElement.setSubtitleTextDelay(textBDelay);
        element.updateAnimation();
        newElement.updateAnimation();
        const insertIndex = this.getElementIndexById(id) + 1;
        this.addElement(newElement, insertIndex);
        this.selectElement(newElement.getId());
        return newElement.getId();
    }

    clearTrack() {
        super.clearTrack();
        this.getAllElements().forEach(ele => {
            ele.clearTrack();
        });
    }

    getFontData() {
        if (this._elements.length > 0) {
            let text = '';
            for (let i = 0; i < this._elements.length; i++) {
                const str = this._elements[i].getTextString();
                text += str;
            }
            const ele = this._elements[0];

            return [
                {
                    fontName: ele.fontFamily,
                    bold: ele.isBold,
                    italic: ele.isItalic,
                    text: text,
                },
            ];
        } else {
            return null;
        }
    }
}

export default FJSubtitle;
